/**
 * Settings → Organisation — the company-prefix section, and nothing else yet.
 *
 * Opened early by the Decision of 2026-09-23 §3a: the Organisation section only, with invites,
 * API keys and billing still Phase 5's. It holds the claim, the evidence upload and the wait,
 * which is every step between a draft and `published` that the dashboard could not reach.
 *
 * The upload is the first browser PUT to the bucket — MinIO's default CORS admitted it on
 * venue L, 2026-09-24. Hetzner's bucket has not been driven from a browser.
 */
import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { CompanyPrefixSummary } from "@packagepage/shared";
import {
  Button,
  Pill,
  Surface,
  SurfaceBody,
  SurfaceHeader,
  Timeline,
} from "@packagepage/ui";

import { problemOf } from "../api/client.js";
import { api } from "../api/procedures.js";
import { putToPresignedUrl } from "../api/upload.js";
import {
  PREFIX_STATE_LABELS,
  prefixPill,
  prefixTimeline,
  uploadPrompt,
} from "./prefix-screen.js";

import "@packagepage/ui/tokens.css";
import "@packagepage/ui/surface.css";
import "@packagepage/ui/button.css";
import "@packagepage/ui/pill.css";
import "@packagepage/ui/timeline.css";
import "./settings.css";

const PREFIXES_KEY = ["prefixes"] as const;

/** The refusal's own words when the server sent one; ours when the request never arrived. */
function messageOf(error: unknown, fallback: string): string {
  return problemOf(error)?.detail ?? fallback;
}

/** Files the document, then PUTs the bytes to the presigned URL. The API never sees the file. */
async function sendCertificate(prefixId: string, file: File): Promise<void> {
  const submission = await api.submitPrefixEvidence({
    prefixId,
    mime: file.type === "" ? "application/octet-stream" : file.type,
    bytes: file.size,
    filename: file.name,
  });
  await putToPresignedUrl(submission.upload, file);
}

function UploadForm({
  prefixId,
  prompt,
  first,
}: {
  readonly prefixId: string;
  readonly prompt: string;
  /** No document filed yet — the send is this card's primary act. */
  readonly first: boolean;
}): ReactNode {
  const client = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const send = useMutation({
    mutationFn: ({ chosen }: { chosen: File; form: HTMLFormElement }) =>
      sendCertificate(prefixId, chosen),
    // A chosen file left in the input after a send files a duplicate on the next click.
    onSuccess: (_result, { form }) => {
      form.reset();
      setFile(null);
    },
    onSettled: () => client.invalidateQueries({ queryKey: PREFIXES_KEY }),
  });
  const inputId = `pp-settings-evidence-${prefixId}`;

  return (
    <form
      className="pp-settings__form"
      onSubmit={(event) => {
        event.preventDefault();
        if (file !== null) send.mutate({ chosen: file, form: event.currentTarget });
      }}
    >
      <label className="pp-settings__label" htmlFor={inputId}>
        {prompt}
      </label>
      <input
        id={inputId}
        className="pp-settings__file"
        type="file"
        name="certificate"
        accept="application/pdf,image/*"
        onChange={(event) => {
          setFile(event.target.files?.[0] ?? null);
        }}
      />
      <Button
        variant={first ? "primary" : "secondary"}
        type="submit"
        disabled={file === null}
        loading={send.isPending}
      >
        {send.isPending ? "Sending…" : "Send for review"}
      </Button>
      {send.isError && (
        <p className="pp-settings__error" role="alert">
          {messageOf(
            send.error,
            "The file did not reach us. Choose it again and send it — nothing else changed.",
          )}
        </p>
      )}
    </form>
  );
}

function PrefixCard({ prefix }: { readonly prefix: CompanyPrefixSummary }): ReactNode {
  const prompt = uploadPrompt(prefix.verification);
  return (
    <Surface
      sectioned
      data-prefix={prefix.prefix}
      data-verification={prefix.verification}
    >
      <SurfaceHeader
        title={prefix.prefix}
        meta={
          <Pill family="prefix" value={prefixPill(prefix.verification)}>
            {PREFIX_STATE_LABELS[prefix.verification]}
          </Pill>
        }
      />
      <SurfaceBody>
        {prefix.verification === "disputed" ? (
          <p className="pp-settings__note">
            Another organisation holds a claim on this prefix. Codes you have already
            printed keep resolving; publishing under it waits until the review decides.
            Send your GS1 licence certificate and we will review both claims.
          </p>
        ) : (
          <Timeline steps={prefixTimeline(prefix, new Date())} />
        )}
        {prompt !== null && (
          <UploadForm
            prefixId={prefix.id}
            prompt={prompt}
            first={prefix.documents.length === 0}
          />
        )}
      </SurfaceBody>
    </Surface>
  );
}

function ClaimForm({ first }: { readonly first: boolean }): ReactNode {
  const client = useQueryClient();
  const [prefix, setPrefix] = useState("");
  const claim = useMutation({
    mutationFn: api.claimPrefix,
    onSuccess: () => {
      setPrefix("");
      return client.invalidateQueries({ queryKey: PREFIXES_KEY });
    },
  });

  return (
    <Surface sectioned>
      <SurfaceHeader
        title={first ? "Claim your company prefix" : "Claim another prefix"}
      />
      <SurfaceBody>
        {first && (
          <p className="pp-settings__note">
            Publishing needs a verified company prefix — the digits every one of your
            GTINs starts with. Claim it here, then send your GS1 licence certificate and a
            person reviews it. Drafts, links and previews all work meanwhile.
          </p>
        )}
        <form
          className="pp-settings__form"
          onSubmit={(event) => {
            event.preventDefault();
            claim.mutate({ prefix: prefix.trim() });
          }}
        >
          <label className="pp-settings__label" htmlFor="pp-settings-prefix">
            Company prefix, 4 to 12 digits
          </label>
          <input
            id="pp-settings-prefix"
            className="pp-settings__input"
            name="prefix"
            inputMode="numeric"
            autoComplete="off"
            value={prefix}
            placeholder="9520123"
            onChange={(event) => {
              setPrefix(event.target.value);
            }}
          />
          <Button
            variant={first ? "primary" : "secondary"}
            type="submit"
            loading={claim.isPending}
          >
            {claim.isPending ? "Claiming…" : "Claim"}
          </Button>
          {claim.isError && (
            <p className="pp-settings__error" role="alert">
              {messageOf(
                claim.error,
                "We could not reach the server. Nothing was claimed.",
              )}
            </p>
          )}
        </form>
      </SurfaceBody>
    </Surface>
  );
}

export function OrganisationSettings(): ReactNode {
  const prefixes = useQuery({ queryKey: PREFIXES_KEY, queryFn: api.companyPrefixes });

  return (
    <section className="pp-settings" data-screen-label="Settings · Organisation">
      <h1 className="pp-settings__title">Organisation</h1>
      <h2 className="pp-settings__section">Company prefixes</h2>
      {prefixes.isPending ? (
        <p className="pp-settings__note">Loading your prefixes…</p>
      ) : prefixes.isError ? (
        <p className="pp-settings__error" role="alert">
          {messageOf(
            prefixes.error,
            "We could not load your prefixes. Reload to try again.",
          )}
        </p>
      ) : (
        <>
          {prefixes.data.prefixes.map((prefix) => (
            <PrefixCard key={prefix.id} prefix={prefix} />
          ))}
          <ClaimForm first={prefixes.data.prefixes.length === 0} />
        </>
      )}
    </section>
  );
}
